/**
 * RunLog — the full actions log view (flow-visualizer-v2 FR-4). A tabulated,
 * sortable, filterable table over EVERY node's {@link ActionRecord}s for a
 * run, plus a footer rollup (row counts, run token total, elapsed). `FlowRunView`
 * (WU-4b, FR-6.1) mounts this in place of the canvas when its header's "Log"
 * toggle is on; this module itself is presentation-only and reusable standalone
 * (a host could render it without the graph at all).
 *
 * Filtering/sorting is delegated entirely to the pure, unit-tested
 * `filterActions` / `sortActions` helpers in `actionLog.ts` (shared with the
 * extension host's mirror) — this file only owns the table chrome, the
 * node/kind/tool dropdown population (derived from the data actually present,
 * per FR-4.3), row expansion, and the footer's token/elapsed rollup math
 * ({@link sumRunTokens} / {@link computeRunElapsedMs}, also used by
 * FlowRunView's header strip so the two never disagree).
 */

import * as React from "react";
import {
  filterActions,
  sortActions,
  type ActionRecord,
  type ActionKind,
  type ActionFilter,
  type ActionSort,
  type ActionSortKey,
} from "./actionLog";
import type { FlowDefinition } from "./types";
import type { NodeRunMeta } from "./FlowRunView";

export interface RunLogProps {
  /** The flow the run belongs to — supplies node labels + the node filter's option list. */
  flow: FlowDefinition;
  /** Per-node structured action log, keyed by nodeId (same shape FlowRunView takes). */
  actions?: Record<string, ActionRecord[]>;
  /** Per-node run details — summed into the footer's token rollup (FR-1.5/4.5). */
  metas?: Record<string, NodeRunMeta>;
  /** Wall-clock snapshot for the elapsed footer stat; defaults to render time. */
  nowMs?: number;
}

/** Run-total token rollup (FR-1.5), summed across every node's {@link NodeRunMeta}. */
export interface RunTokenTotals {
  input: number;
  output: number;
  total: number;
}

/**
 * Sum per-node token usage into a run total (FR-1.5, FR-4.5, FR-6.1). A node
 * with only `totalTokens` (no split) still counts toward `total`; `undefined`
 * when no node has any token data yet (nothing to show).
 */
export function sumRunTokens(
  metas: Record<string, NodeRunMeta> | undefined
): RunTokenTotals | undefined {
  let input = 0;
  let output = 0;
  let total = 0;
  let any = false;
  for (const m of Object.values(metas ?? {})) {
    const hasAny =
      m.inputTokens != null || m.outputTokens != null || m.totalTokens != null;
    if (!hasAny) {
      continue;
    }
    any = true;
    const tIn = m.inputTokens ?? 0;
    const tOut = m.outputTokens ?? 0;
    input += tIn;
    output += tOut;
    total += m.totalTokens ?? tIn + tOut;
  }
  return any ? { input, output, total } : undefined;
}

/**
 * Run elapsed time (FR-4.5, FR-6.1): earliest node `startedAt` to the latest
 * known end — a finished node's `startedAt + durationMs`, or `nowMs` for a
 * node still running (no `durationMs` yet). Self-contained: needs no separate
 * "is the run still running" flag, so it stays correct for a finished run
 * (every node has a `durationMs`) without a live clock. `undefined` when no
 * node has started yet.
 */
export function computeRunElapsedMs(
  metas: Record<string, NodeRunMeta> | undefined,
  nowMs: number = Date.now()
): number | undefined {
  let earliest: number | undefined;
  let latest: number | undefined;
  for (const m of Object.values(metas ?? {})) {
    if (m.startedAt == null) {
      continue;
    }
    earliest = earliest == null ? m.startedAt : Math.min(earliest, m.startedAt);
    const end = m.durationMs != null ? m.startedAt + m.durationMs : nowMs;
    latest = latest == null ? end : Math.max(latest, end);
  }
  if (earliest == null || latest == null) {
    return undefined;
  }
  return Math.max(0, latest - earliest);
}

// ── Local display helpers (mirrors FlowRunView's private fmtDuration/fmtClock/
// baseName — small pure formatters are kept per-file in this package rather
// than factored into a shared util, same precedent as those three). ─────────

function fmtDuration(ms?: number): string {
  if (ms == null) return "";
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 90) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

function fmtClock(at?: number): string {
  if (!at) return "—";
  const d = new Date(at);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function baseName(p: string): string {
  const parts = p.split(/[\\/]/);
  return parts[parts.length - 1] || p;
}

/** Single-glyph icon per action kind (mirrors FlowRunView's drill-down icons). */
const KIND_ICON: Record<ActionKind, string> = {
  tool: "⚙",
  edit: "✎",
  read: "▤",
  command: "❯",
  "thinking-marker": "…",
  transition: "→",
  error: "!",
  approval: "✓",
  result: "◆",
};

const SORTABLE_COLUMNS: Array<{ key: ActionSortKey; label: string }> = [
  { key: "time", label: "Time" },
  { key: "node", label: "Node" },
  { key: "kind", label: "Kind" },
  { key: "tool", label: "Tool" },
];

/** Sortable `<th>` — a real button inside so it's a native keyboard target (FR-4.6). */
function SortTh({
  col,
  label,
  sort,
  onSort,
}: {
  col: ActionSortKey;
  label: string;
  sort: ActionSort;
  onSort: (col: ActionSortKey) => void;
}) {
  const active = sort.by === col;
  const dir = active ? sort.dir ?? "desc" : undefined;
  return (
    <th
      scope="col"
      className={`fn-run-log-th${active ? " active" : ""}`}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        className="fn-run-log-sort-btn"
        onClick={() => onSort(col)}
        aria-label={`Sort by ${label}${active ? (dir === "asc" ? ", ascending" : ", descending") : ""}`}
      >
        {label}
        {active ? (
          <span className="fn-run-log-sort-caret" aria-hidden="true">
            {dir === "asc" ? "▲" : "▼"}
          </span>
        ) : null}
      </button>
    </th>
  );
}

/** One log row; click (or Enter/Space) expands the capped `detail` beneath it (FR-4.4). */
function LogRow({
  record,
  nodeLabel,
  expanded,
  onToggle,
}: {
  record: ActionRecord;
  nodeLabel: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const hasDetail = !!record.detail;
  return (
    <>
      <tr
        className={`fn-run-log-row fn-run-log-row-kind-${record.kind}${
          hasDetail ? " fn-run-log-row-expandable" : ""
        }`}
        onClick={hasDetail ? onToggle : undefined}
        onKeyDown={
          hasDetail
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle();
                }
              }
            : undefined
        }
        tabIndex={hasDetail ? 0 : undefined}
        role={hasDetail ? "button" : undefined}
        aria-expanded={hasDetail ? expanded : undefined}
      >
        <td className="fn-run-log-td-time">{fmtClock(record.at)}</td>
        <td className="fn-run-log-td-node" title={record.nodeId}>
          {nodeLabel}
        </td>
        <td className="fn-run-log-td-kind">
          <span className="fn-run-log-kind-icon" aria-hidden="true">
            {KIND_ICON[record.kind]}
          </span>
          {record.kind}
        </td>
        <td className="fn-run-log-td-tool">{record.tool ?? "—"}</td>
        <td className="fn-run-log-td-summary">{record.summary}</td>
        <td className="fn-run-log-td-duration">
          {record.durationMs != null ? fmtDuration(record.durationMs) : "—"}
        </td>
        <td className="fn-run-log-td-file" title={record.file}>
          {record.file ? baseName(record.file) : "—"}
        </td>
      </tr>
      {expanded && hasDetail ? (
        <tr className="fn-run-log-detail-row">
          <td colSpan={7}>
            <pre className="fn-run-log-detail">{record.detail}</pre>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * The actions log table (FR-4): sortable/filterable/searchable over every
 * node's ActionRecords, with a footer rollup. Pure over its props — no
 * internal fetching, no host coupling; `FlowRunView` feeds it the same
 * `actions`/`metas` it already threads to the inspector and bubble.
 */
export function RunLog({ flow, actions = {}, metas = {}, nowMs }: RunLogProps) {
  const nodeLabelOf = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const n of flow.nodes ?? []) {
      m.set(n.nodeId, n.name || n.nodeId);
    }
    return (nodeId: string) => m.get(nodeId) ?? nodeId;
  }, [flow.nodes]);

  const all = React.useMemo(() => Object.values(actions).flat(), [actions]);

  const [nodeId, setNodeId] = React.useState<string | undefined>(undefined);
  const [kind, setKind] = React.useState<ActionKind | undefined>(undefined);
  const [tool, setTool] = React.useState<string | undefined>(undefined);
  const [search, setSearch] = React.useState("");
  const [sort, setSort] = React.useState<ActionSort>({ by: "time", dir: "desc" });
  const [expandedSeq, setExpandedSeq] = React.useState<number | null>(null);

  // Dropdown options are populated FROM THE DATA (FR-4.3) — nodes/kinds/tools
  // actually seen, not a static enum. Node options also fold in every flow
  // node (even ones with zero actions yet) so the filter is usable early.
  const nodeOptions = React.useMemo(() => {
    const ids = new Set<string>();
    for (const n of flow.nodes ?? []) ids.add(n.nodeId);
    for (const r of all) ids.add(r.nodeId);
    return Array.from(ids)
      .map((id) => ({ id, label: nodeLabelOf(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [flow.nodes, all, nodeLabelOf]);
  const kindOptions = React.useMemo(() => {
    const kinds = new Set<ActionKind>();
    for (const r of all) kinds.add(r.kind);
    return Array.from(kinds).sort();
  }, [all]);
  const toolOptions = React.useMemo(() => {
    const tools = new Set<string>();
    for (const r of all) if (r.tool) tools.add(r.tool);
    return Array.from(tools).sort();
  }, [all]);

  const filter: ActionFilter = { nodeId, kind, tool, search: search || undefined };
  const filtered = React.useMemo(
    () => sortActions(filterActions(all, filter), sort),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, nodeId, kind, tool, search, sort]
  );

  const onSort = (col: ActionSortKey) => {
    setSort((prev) =>
      prev.by === col
        ? { by: col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { by: col, dir: col === "time" ? "desc" : "asc" }
    );
  };

  const tokenTotals = sumRunTokens(metas);
  const elapsedMs = computeRunElapsedMs(metas, nowMs);

  return (
    <div className="fn-run-log" role="region" aria-label="Actions log">
      <div className="fn-run-log-filters">
        <label className="fn-run-log-filter">
          <span className="fn-run-log-filter-label">Node</span>
          <select
            value={nodeId ?? ""}
            onChange={(e) => setNodeId(e.target.value || undefined)}
            aria-label="Filter by node"
          >
            <option value="">All nodes</option>
            {nodeOptions.map((n) => (
              <option key={n.id} value={n.id}>
                {n.label}
              </option>
            ))}
          </select>
        </label>
        <label className="fn-run-log-filter">
          <span className="fn-run-log-filter-label">Kind</span>
          <select
            value={kind ?? ""}
            onChange={(e) => setKind((e.target.value || undefined) as ActionKind | undefined)}
            aria-label="Filter by kind"
          >
            <option value="">All kinds</option>
            {kindOptions.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="fn-run-log-filter">
          <span className="fn-run-log-filter-label">Tool</span>
          <select
            value={tool ?? ""}
            onChange={(e) => setTool(e.target.value || undefined)}
            aria-label="Filter by tool"
          >
            <option value="">All tools</option>
            {toolOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="fn-run-log-filter fn-run-log-search">
          <span className="fn-run-log-filter-label">Search</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="summary, detail, file…"
            aria-label="Search actions by summary, detail or file"
          />
        </label>
      </div>
      <div className="fn-run-log-table-wrap">
        <table className="fn-run-log-table">
          <thead>
            <tr>
              {SORTABLE_COLUMNS.slice(0, 4).map((c) => (
                <SortTh key={c.key} col={c.key} label={c.label} sort={sort} onSort={onSort} />
              ))}
              <th scope="col" className="fn-run-log-th fn-run-log-th-plain">
                Summary
              </th>
              <SortTh col="duration" label="Duration" sort={sort} onSort={onSort} />
              <th scope="col" className="fn-run-log-th fn-run-log-th-plain">
                File
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td className="fn-run-log-empty" colSpan={7}>
                  No actions match.
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <LogRow
                  key={r.seq}
                  record={r}
                  nodeLabel={nodeLabelOf(r.nodeId)}
                  expanded={expandedSeq === r.seq}
                  onToggle={() => setExpandedSeq((s) => (s === r.seq ? null : r.seq))}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="fn-run-log-footer">
        <span className="fn-run-log-footer-count">
          {filtered.length.toLocaleString()} / {all.length.toLocaleString()} actions
        </span>
        {tokenTotals ? (
          <span className="fn-run-log-footer-tokens">
            {tokenTotals.total.toLocaleString()} tokens
            <span className="fn-run-log-footer-sub">
              {" "}
              ({tokenTotals.input.toLocaleString()} in / {tokenTotals.output.toLocaleString()} out)
            </span>
          </span>
        ) : null}
        {elapsedMs != null ? (
          <span className="fn-run-log-footer-elapsed">{fmtDuration(elapsedMs)} elapsed</span>
        ) : null}
      </div>
    </div>
  );
}
